// Org-scoped paths (reset PR2). `/o/:slug` is Home; the library moved to `/o/:slug/library`,
// with its filter and search in the query string so a library view is a shareable link.

export type LibraryFilter = "mine" | "all" | "shared";

export const LIBRARY_FILTERS: readonly LibraryFilter[] = ["mine", "all", "shared"];

/** Default when the URL has no (or an unknown) `filter`: what the library showed before PR2. */
export const DEFAULT_LIBRARY_FILTER: LibraryFilter = "mine";

export function parseLibraryFilter(raw: string | null): LibraryFilter {
  return LIBRARY_FILTERS.includes(raw as LibraryFilter) ? (raw as LibraryFilter) : DEFAULT_LIBRARY_FILTER;
}

export function homePath(slug: string): string {
  return `/o/${slug}`;
}

export function libraryPath(slug: string, params?: { filter?: LibraryFilter; q?: string }): string {
  const qs = new URLSearchParams();
  if (params?.filter) qs.set("filter", params.filter);
  if (params?.q) qs.set("q", params.q);
  const s = qs.toString();
  return `/o/${slug}/library${s ? `?${s}` : ""}`;
}

/** FR-P14: upload an existing video or audio file into this org. */
export function uploadPath(slug: string): string {
  return `/o/${encodeURIComponent(slug)}/upload`;
}

/** F7 / FR-N7: the full notification feed (the bell's "See all"). */
export function notificationsPath(slug: string): string {
  return `/o/${encodeURIComponent(slug)}/notifications`;
}

/** F2: the trash view, at the bottom of the sidebar. */
export function trashPath(slug: string): string {
  return `/o/${slug}/trash`;
}

/** F2: a folder's contents, same card grid as the library. */
export function folderPath(slug: string, folderId: string): string {
  return `/o/${slug}/folders/${folderId}`;
}

/** F2: transcript search results. `q` is required server-side, but left optional here so a
 * caller can build the base path and append it itself. */
export function searchPath(slug: string, q?: string): string {
  const qs = q ? `?q=${encodeURIComponent(q)}` : "";
  return `/o/${slug}/search${qs}`;
}

/** Query parameters that mean "this was a library link" on the old `/o/:slug` library route.
 * The pre-PR2 library kept its filter and search in component state, so no older parameter
 * names exist; `q` and `filter` are the library's own. Anything else (e.g. the session's
 * `?preview=1`) stays on Home. */
export const LEGACY_LIBRARY_PARAMS = ["q", "filter"] as const;

/** Where an old `/o/:slug?...` deep link should go, or null to render Home. */
export function legacyLibraryRedirect(slug: string, search: string): string | null {
  const params = new URLSearchParams(search);
  if (!LEGACY_LIBRARY_PARAMS.some((p) => params.has(p))) return null;
  return `/o/${slug}/library?${params.toString()}`;
}

/** Meetings (calendar, auto-record rules, notetaker): the sidebar's Meetings entry. */
export function meetingsPath(slug: string): string {
  return `/o/${slug}/meetings`;
}

/** Live notes (FR-M5): how to use the extension, reached from the Meetings tabs since reset PR3. */
export function liveNotesPath(slug: string): string {
  return `/o/${slug}/live-meeting`;
}

/** The FR-X4 panel test harness (staff only): drives a real live session without the extension. */
export function liveNotesTestPath(slug: string): string {
  return `/o/${slug}/live-meeting/test`;
}

/** Meeting notes for one meeting recording (reset PR3 scaffold; F1 fills it). */
export function notesPath(slug: string, grackleId: string): string {
  return `/o/${slug}/meetings/${grackleId}/notes`;
}

/** Admin AI settings, where an org admin connects a language model. */
export function adminAiPath(slug: string): string {
  return `/o/${slug}/admin/ai`;
}

/** A page of the org admin console: `adminPath(slug)` is the Overview, `adminPath(slug, "sso")` SSO. */
export function adminPath(slug: string, page?: string): string {
  return page ? `/o/${slug}/admin/${page}` : `/o/${slug}/admin`;
}
